import { SerpAiDialog } from './SerpAiDialog';
import { SerpPageContent } from './SerpPageContent';
import { SerpRelevance } from './SerpRelevance';
import { competitors } from '../../../api/src/fakes/serp-indicators';
import { Link as RouterLink } from 'react-router';
import { EditorOnly, useCanEdit } from './EditorOnly';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  TextField,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { resourcePlatform } from '../../../api/src/registry/registry-platform';
import { MENTION_LABELS, type MentionSentiment } from '../../../api/src/fakes/serp-classification';
import type { FakeDecision, FakeVerdict } from '../../../api/src/fakes/fakes.types';
import type { FakesResult } from '../api/types';
import { api } from '../api/client';
import { Box, Stack, Typography } from './mui-system';
import {
  serpTextColor,
  competitorTone,
  fakeDate,
  fakeError,
  fakeLabels,
} from './fakes-presentation';

export function FakeResultDrawer({
  workspaceId,
  result,
  query,
  capturedAt,
  previousCapturedAt,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  result: FakesResult;
  query?: string;
  capturedAt?: string | null;
  previousCapturedAt?: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const editable = useCanEdit('fakes.edit');
  const [aiOpen, setAiOpen] = useState(false);
  const [history, setHistory] = useState<FakeDecision[] | null>(null);
  const [historyError, setHistoryError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState<'url' | 'host' | 'channel'>('url');
  const [verdict, setVerdict] = useState<FakeVerdict>(
    result.decision?.verdict === 'external'
      ? 'unreviewed'
      : (result.decision?.verdict ?? 'unreviewed'),
  );
  const [sentiment, setSentiment] = useState<MentionSentiment | ''>(
    result.mention?.sentiment ?? '',
  );
  const [reason, setReason] = useState('');
  const mounted = useRef(true),
    saving = useRef(false);
  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    void api
      .fakeDecisions(workspaceId, result.id)
      .then((rows) => {
        if (!cancelled) setHistory(rows);
      })
      .catch((e) => {
        if (!cancelled) setHistoryError(fakeError(e));
      });
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [workspaceId, result.id]);
  async function save() {
    if (saving.current || !reason.trim() || !result.reviewVersion) return;
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      await api.decideFake(workspaceId, result.id, {
        scope,
        verdict,
        reason,
        expectedVersion: result.reviewVersion,
        mentionSentiment:
          scope === 'url' && ['about', 'unreviewed', 'fake'].includes(verdict)
            ? sentiment || null
            : null,
      });
      if (!mounted.current) return;
      await onSaved();
      if (mounted.current) onClose();
    } catch (e) {
      if (mounted.current) setError(fakeError(e));
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <Drawer
      anchor="right"
      open
      onClose={() => !busy && onClose()}
      slotProps={{
        paper: {
          sx: { width: { xs: '100%', sm: 440 }, maxWidth: '100%' },
          role: 'dialog',
          'aria-label': 'Разобрать ресурс',
        },
      }}
    >
      <Stack p={3} gap={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h3">Разобрать ресурс</Typography>
          <IconButton aria-label="Закрыть разбор" disabled={busy} onClick={onClose}>
            <Icon icon="solar:close-circle-linear" />
          </IconButton>
        </Stack>
        {query && (
          <Typography color="text.secondary" fontSize={13}>
            {query} · Top-{result.rank}
          </Typography>
        )}
        <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
          {result.telegramUsername ? '@' + result.telegramUsername : result.hostname}
        </Typography>
        <Link
          href={result.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: 12, overflowWrap: 'anywhere' }}
        >
          {result.url}
        </Link>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Chip label={fakeLabels[result.classification]} />
          {competitors(result).map((r) => (
            <Chip
              key={r.key}
              label={`Конкурент · ${r.title}`}
              sx={(theme) => ({
                color: serpTextColor(theme, competitorTone(theme)),
                borderColor: competitorTone(theme),
              })}
              variant="outlined"
            />
          ))}
          {result.mention && (
            <Chip
              variant="outlined"
              label={`О нас · ${result.mention.sentiment ? MENTION_LABELS[result.mention.sentiment] : 'Контекст не оценён'}`}
            />
          )}
          {result.domainStatus && <Chip variant="outlined" label={result.domainStatus} />}
        </Stack>
        <Typography fontSize={13}>{result.reason}</Typography>
        {result.workspaceResources?.map((entry) => (
          <Typography key={entry.key} fontSize={12} color="text.secondary">
            <Link
              component={RouterLink}
              to={
                entry.key.startsWith('manual:')
                  ? `/registry?entry=${encodeURIComponent(entry.key.slice(7))}`
                  : `/registry?view=search&q=${encodeURIComponent(entry.address)}`
              }
            >
              {entry.title}
            </Link>
            {entry.revision ? ` · версия ${entry.revision}` : ''} ·{' '}
            {entry.scope === 'competitor_subdomain'
              ? `Поддомен конкурента ${entry.address}`
              : entry.ownership === 'owned'
                ? 'Наш'
                : entry.ownership === 'third_party'
                  ? 'Сторонний'
                  : 'Не установлена'}{' '}
            ·{' '}
            {entry.scope === 'competitor_subdomain'
              ? 'только метка конкурента'
              : entry.scope === 'host'
                ? 'точный домен'
                : entry.scope === 'telegram_directory'
                  ? 'канал, о котором страница TGStat'
                  : 'конкретный ресурс'}
          </Typography>
        ))}
        {result.telegramChannel && (
          <Typography fontSize={12} color="text.secondary">
            Канал в реестре: {result.telegramChannel.title || '@' + result.telegramChannel.username}{' '}
            · версия {result.telegramChannel.revision}
          </Typography>
        )}
        {result.donorHostKnown && !result.donorArticleId && (
          <Alert severity="info">
            В «Донорах» есть другие записи этого hostname. Они не подтверждают текущий URL.
          </Alert>
        )}
        {capturedAt && (
          <Typography color="text.secondary" fontSize={12}>
            Снято: {fakeDate(capturedAt)}
            {previousCapturedAt ? ` · Сравнение: ${fakeDate(previousCapturedAt)}` : ''}
          </Typography>
        )}
        <Typography fontSize={13}>
          Позиция URL: {result.rank}
          {result.delta !== null
            ? ` · ${result.delta > 0 ? '↑' : result.delta < 0 ? '↓' : '—'} ${result.delta ? Math.abs(result.delta) : ''}`
            : ''}
        </Typography>
        <SerpRelevance
          workspaceId={workspaceId}
          result={result}
          onSaved={onSaved}
          onClose={onClose}
        />
        <Typography fontWeight={700}>Связь с брендом</Typography>
        <Typography fontSize={12} color="text.secondary">
          Наши ресурсы и конкуренты задаются в{' '}
          <Link
            component={RouterLink}
            to={`/registry?view=search&q=${encodeURIComponent(resourcePlatform(result.hostname) === 'web' ? result.hostname : (result.telegramUsername ?? result.url))}`}
          >
            Реестре
          </Link>
          . Донор подтверждается публикацией в{' '}
          <Link component={RouterLink} to="/donors">
            Донорах
          </Link>{' '}
          или записью конкретного ресурса в Реестре.
        </Typography>
        <Typography fontSize={12} color="text.secondary">
          Заголовок и сниппет поисковой выдачи не сохранены. Содержимое страницы можно получить
          отдельно.
        </Typography>
        <SerpPageContent
          key={workspaceId + result.id}
          workspaceId={workspaceId}
          resultId={result.id}
        />
        <Button variant="outlined" onClick={() => setAiOpen(true)}>
          ИИ-подсказка
        </Button>
        {aiOpen && (
          <SerpAiDialog
            key={workspaceId + result.id}
            workspaceId={workspaceId}
            resultId={result.id}
            candidates={[result]}
            onClose={() => setAiOpen(false)}
            onSaved={onSaved}
          />
        )}
        <TextField
          select
          label="Область решения"
          value={scope}
          disabled={!editable || busy || ['about', 'namesake', 'unrelated'].includes(verdict)}
          onChange={(e) => setScope(e.target.value as typeof scope)}
        >
          <MenuItem value="url">Только этот URL</MenuItem>
          {result.telegramUsername ? (
            <MenuItem value="channel">Весь канал @{result.telegramUsername}</MenuItem>
          ) : resourcePlatform(new URL(result.url).hostname) === 'web' ? (
            <MenuItem value="host">Весь hostname {result.hostname}</MenuItem>
          ) : null}
        </TextField>
        {scope === 'host' && (
          <Alert severity="warning">
            Решение применяется ко всем URL этого hostname. Для общих платформ используйте
            конкретный URL.
          </Alert>
        )}
        <TextField
          select
          label="Решение"
          value={verdict}
          disabled={!editable || busy}
          onChange={(e) => {
            const next = e.target.value as FakeVerdict;
            setVerdict(next);
            if (['about', 'namesake', 'unrelated'].includes(next)) setScope('url');
            if (['namesake', 'unrelated'].includes(next)) setSentiment('');
          }}
        >
          <MenuItem value="unreviewed">Не разобрано</MenuItem>
          <MenuItem value="about">О нас</MenuItem>
          <MenuItem value="namesake">Совпадение названия</MenuItem>
          <MenuItem value="unrelated">Посторонний результат</MenuItem>
          <MenuItem value="fake">Подтверждённый подражатель / фейк</MenuItem>
        </TextField>
        {scope === 'url' && ['about', 'unreviewed', 'fake'].includes(verdict) && (
          <TextField
            select
            label="Контекст упоминания"
            value={sentiment}
            disabled={!editable || busy}
            helperText="Относится только к этому URL. Не меняет цвет нашего ресурса, донора или конкурента."
            onChange={(e) => setSentiment(e.target.value as MentionSentiment | '')}
          >
            <MenuItem value="">Не оценён</MenuItem>
            {Object.entries(MENTION_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {verdict === 'fake' && (
          <Alert severity="warning">
            Красный цвет — подтверждённое решение команды. Укажите признаки подражания; негативное
            упоминание само по себе не означает фейк.
          </Alert>
        )}
        {!result.reviewVersion && (
          <Alert severity="warning">Обновите карту перед сохранением решения.</Alert>
        )}
        <TextField
          label="Основание решения"
          value={reason}
          disabled={!editable || busy}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 2000 } }}
        />
        {error && <Alert severity="error">{error}</Alert>}
        <EditorOnly permissions="fakes.edit">
          <Button
            variant="contained"
            disabled={busy || !reason.trim() || !result.reviewVersion}
            onClick={() => void save()}
          >
            Сохранить решение
          </Button>
        </EditorOnly>
        <Typography fontWeight={700} mt={1}>
          Последние решения
        </Typography>
        {historyError && <Alert severity="error">{historyError}</Alert>}
        {!history && !historyError && <LinearProgress />}
        {history?.length === 0 && <Typography color="text.secondary">Решений пока нет</Typography>}
        {history?.map((item) => (
          <Box key={item.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
            <Typography fontSize={12}>
              {fakeDate(item.createdAt)} · {fakeLabels[item.verdict]}
              {item.mentionSentiment ? ` · О нас: ${MENTION_LABELS[item.mentionSentiment]}` : ''}
            </Typography>
            <Typography fontSize={13}>{item.reason}</Typography>
            <Typography fontSize={11} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {item.resourceKey} · {item.actorId}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Drawer>
  );
}
